import { getExplorerTxUrl, type SupportedChainId } from "@breakout/sdk"
import { exhaustiveGuard } from "@breakout/shared/utils"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@breakout/ui/components/Dialog"
import { CheckCircle, ExternalLink, Loader2, XCircle } from "lucide-react"

export type TransactionStatus = "idle" | "pending" | "confirming" | "confirmed" | "error"

interface TransactionStatusDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	status: TransactionStatus
	txHash?: string
	chainId: SupportedChainId
	title?: string
	description?: string
	errorMessage?: string
	/** Number of operations in the batch */
	operationCount?: number
}

export function TransactionStatusDialog({
	open,
	onOpenChange,
	status,
	txHash,
	chainId,
	title,
	description,
	errorMessage,
	operationCount = 1,
}: TransactionStatusDialogProps) {
	const explorerUrl = txHash ? getExplorerTxUrl(chainId, txHash) : null

	const getStatusContent = () => {
		switch (status) {
			case "pending":
				return {
					icon: <Loader2 aria-hidden="true" className="size-12 animate-spin text-gray-500" />,
					title: title || "Awaiting Confirmation",
					description: description || "Please confirm the transaction in your wallet...",
				}
			case "confirming":
				return {
					icon: <Loader2 aria-hidden="true" className="size-12 animate-spin text-amber-500" />,
					title: "Transaction Submitted",
					description: `Waiting for confirmation on chain...${operationCount > 1 ? ` (${operationCount} operations)` : ""}`,
				}
			case "confirmed":
				return {
					icon: <CheckCircle aria-hidden="true" className="size-12 text-green-500" />,
					title: "Transaction Confirmed",
					description: `Successfully executed${operationCount > 1 ? ` ${operationCount} operations` : ""} in a single transaction.`,
				}
			case "error":
				return {
					icon: <XCircle aria-hidden="true" className="size-12 text-red-500" />,
					title: "Transaction Failed",
					description: errorMessage || "The transaction failed. Please try again.",
				}
			case "idle":
				return null
			default:
				return exhaustiveGuard(status)
		}
	}

	const content = getStatusContent()

	// Early return if dialog shouldn't be visible - prevents Portal from blocking clicks
	if (!open || !content) {
		return null
	}

	const canClose = status === "confirmed" || status === "error"

	return (
		<Dialog onOpenChange={canClose ? onOpenChange : undefined} open>
			<DialogContent
				className="sm:max-w-md"
				onEscapeKeyDown={e => !canClose && e.preventDefault()}
				onPointerDownOutside={e => !canClose && e.preventDefault()}
			>
				<DialogHeader className="items-center text-center">
					<div className="mb-4">{content.icon}</div>
					<DialogTitle>{content.title}</DialogTitle>
					<DialogDescription>{content.description}</DialogDescription>
				</DialogHeader>

				{txHash && (
					<div className="flex items-center justify-center gap-2 rounded-lg bg-gray-100 p-3">
						<code className="font-mono text-gray-600 text-sm">
							{txHash.slice(0, 10)}...{txHash.slice(-8)}
						</code>
						{explorerUrl && (
							<a
								aria-label="View transaction on block explorer (opens in new tab)"
								className="inline-flex items-center gap-1 text-gray-600 text-sm hover:text-gray-500"
								href={explorerUrl}
								rel="noopener noreferrer"
								target="_blank"
							>
								View on Explorer
								<ExternalLink aria-hidden="true" className="size-3" />
							</a>
						)}
					</div>
				)}

				{canClose && (
					<DialogFooter className="justify-center">
						<button
							className="rounded-lg bg-gray-900 px-4 py-2 font-medium text-sm text-white hover:bg-gray-800"
							onClick={() => onOpenChange(false)}
							type="button"
						>
							{status === "confirmed" ? "Done" : "Close"}
						</button>
					</DialogFooter>
				)}
			</DialogContent>
		</Dialog>
	)
}
